/**
 * Modal shown when a force update is required.
 *
 * Displays a countdown timer and auto-refreshes when it reaches zero.
 * Users can also click "Refresh Now" to update immediately.
 */
import { useState, useEffect } from "react";

import { Modal, ModalBody, ModalFooter } from "@/components/Modal";
import { useForceUpdateStore } from "@/stores/forceUpdate";
import { buttonVariants } from "@markeroo/theme";
import { ui } from "@/strings";

const COUNTDOWN_SECONDS = 10;

export function ForceUpdateModal() {
  const showModal = useForceUpdateStore((state) => state.showModal);
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);

  // Reset countdown when modal opens
  useEffect(() => {
    if (showModal) {
      setCountdown(COUNTDOWN_SECONDS);
    }
  }, [showModal]);

  // Countdown timer
  useEffect(() => {
    if (!showModal) return;

    if (countdown === 0) {
      window.location.reload();
      return;
    }

    const timer = setTimeout(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [showModal, countdown]);

  const handleRefreshNow = () => {
    window.location.reload();
  };

  // Don't render anything if modal shouldn't be shown
  if (!showModal) return null;

  return (
    <Modal
      isOpen={showModal}
      onClose={() => {
        // Prevent closing - user must refresh
      }}
      size="sm"
      role="alertdialog"
      aria-label={ui.forceUpdate.title}
    >
      <ModalBody>
        <div className="text-center pt-6">
          {/* eslint-disable-next-line no-restricted-syntax */}
          <p className="text-5xl mb-6">🎉</p>
          <h2 className="text-lg font-semibold text-brand-950 mb-2">{ui.forceUpdate.title}</h2>
          <p className="text-sm text-theme-secondary">{ui.forceUpdate.message}</p>
          <p className="my-8 text-lg font-semibold text-theme-secondary">
            {ui.forceUpdate.countdownPrefix} {countdown} {ui.forceUpdate.countdownSuffix}
          </p>
        </div>
      </ModalBody>
      <ModalFooter>
        <button onClick={handleRefreshNow} className={buttonVariants.primary}>
          {ui.forceUpdate.refreshNow}
        </button>
      </ModalFooter>
    </Modal>
  );
}
